// What did Scout do? — `intake_events`, newest first, filterable.
//
// #175 removed Salesforce Tasks, so the Lightning timeline is no longer the audit trail. This
// is. The one thing it must never do is make a `written=false` knock look like a successful
// one: those rows carry an amber edge and a "not written" pill, because nothing about them
// reached Salesforce and reading the outcome column alone would say `created`.

import { useState } from 'react';
import type { IntakeEventRow, IntakeEventsPayload } from '../api/types';
import { SALESFORCE_ACCOUNT_URL, formatCount } from '../lib/format';
import { StatePill } from './AdminSection';
import { LinkedText } from '../components/LinkedText';
import { WRITTEN_LABEL, WRITTEN_TONE, formatStamp, writtenState } from './format.ts';

export interface KnockFilters {
  outcome: string;
  source: string;
  reason: string;
  domain: string;
}

export const EMPTY_KNOCK_FILTERS: KnockFilters = {
  outcome: '',
  source: '',
  reason: '',
  domain: '',
};

function KnockRow({ event }: { event: IntakeEventRow }) {
  const [open, setOpen] = useState(false);
  const written = writtenState(event.written);

  return (
    <>
      <tr className={'acct knockrow ' + written}>
        <td>
          <button type="button" className="rowbtn" onClick={() => setOpen(!open)}>
            <span className="acctname">{event.domain}</span>
            <div className="sm"><LinkedText text={event.evidence} /></div>
          </button>
        </td>
        <td>
          <span className="chip">{event.outcome}</span>
          {event.reason ? <div className="sm">{event.reason}</div> : null}
        </td>
        <td><StatePill tone={WRITTEN_TONE[written]} label={WRITTEN_LABEL[written]} /></td>
        <td>{event.source}</td>
        <td>
          {event.account_id ? (
            <a
              className="mono"
              href={SALESFORCE_ACCOUNT_URL + event.account_id + '/view'}
              target="_blank"
              rel="noreferrer"
            >
              {event.account_id}
            </a>
          ) : <span className="dash">—</span>}
        </td>
        <td>{event.owner || <span className="dash">—</span>}</td>
        <td>{event.actor_email || <span className="dash">—</span>}</td>
        <td>{formatStamp(event.created_at)}</td>
      </tr>
      {open ? (
        <tr className="detail">
          <td colSpan={8}>
            <div className="dh">The reply as it was stored</div>
            <pre className="rawjson">{JSON.stringify(event.reply, null, 2)}</pre>
            <p className="sm">External id: {event.external_id}</p>
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function IntakeEventsTable({
  payload,
  filters,
  onFilters,
}: {
  payload: IntakeEventsPayload | undefined;
  filters: KnockFilters;
  onFilters: (next: KnockFilters) => void;
}) {
  const events = payload?.events ?? [];

  return (
    <>
      <div className="adminfilters">
        <label className="field">
          <span className="fieldlab">Outcome</span>
          <select
            value={filters.outcome}
            onChange={event => onFilters({ ...filters, outcome: event.target.value })}
          >
            <option value="">Every outcome</option>
            {(payload?.outcomes ?? []).map(value => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="fieldlab">Source</span>
          <select
            value={filters.source}
            onChange={event => onFilters({ ...filters, source: event.target.value })}
          >
            <option value="">Every source</option>
            {(payload?.sources ?? []).map(value => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="fieldlab">Reason</span>
          <select
            value={filters.reason}
            onChange={event => onFilters({ ...filters, reason: event.target.value })}
          >
            <option value="">Every reason</option>
            {(payload?.reasons ?? []).map(value => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="fieldlab">Domain</span>
          <input
            type="search"
            placeholder="acme.com"
            value={filters.domain}
            onChange={event => onFilters({ ...filters, domain: event.target.value })}
          />
        </label>
        <button
          type="button"
          className="btn"
          onClick={() => onFilters(EMPTY_KNOCK_FILTERS)}
        >
          Clear
        </button>
        {payload?.truncated ? (
          <span className="admintrunc">
            Showing the newest {formatCount(payload.limit)} knocks — there are more. Narrow the
            filters to see further back.
          </span>
        ) : null}
      </div>
      {events.length === 0 ? (
        <p className="briefempty">
          No knock matches. `intake_events` is append-only, so an empty result is a filter that
          is too narrow rather than a row that was removed.
        </p>
      ) : (
        <div className="tablewrap">
          <table className="booktable">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Outcome</th>
                <th>Written</th>
                <th>Source</th>
                <th>Account</th>
                <th>Owner</th>
                <th>Actor</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {events.map(event => <KnockRow key={event.event_id} event={event} />)}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
